// ==========================
// src/components/Location.jsx
// - Acordeones controlados (solo 1 abierto)
// - Al abrir una región: (opcional) previsualiza la primera agencia
// - Botones debajo del texto (sin overlap) y sin duplicar bloque móvil
// ==========================
import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack,
  Button,
  Divider,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlaceIcon from "@mui/icons-material/Place";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MapIcon from "@mui/icons-material/Map";
import NearMeIcon from '@mui/icons-material/NearMe';

// Datos de agencias
const LOCATIONS = [
  {
    region: "Quito",
    agencies: [
      {
        name: "Agencia La Prensa",
        address: "Av. Prensa y Florida, Quito",
        mapsUrl: "",
        embedSrc: "",
        phone: "593994534400",
      },
      {
        name: "Agencia El Inca",
        address: "Av. El Inca y 6 de Diciembre, Quito",
        mapsUrl: "",
        embedSrc: "",
        phone: "593979056177",
      },
      {
        name: "Agencia Amazonas",
        address: "Av. Amazonas y Roca, Quito",
        mapsUrl: "",
        embedSrc: "",
        phone: "593998388739",
      },
      {
        name: "Agencia 10 de Agosto",
        address: "Av. 10 de Agosto y Alonso de Mercadillo, Quito",
        mapsUrl: "",
        embedSrc: "",
        phone: "593980595725",
      },
      {
        name: "Agencia Calderón",
        address: "Miguel de Guarderas y Faustino Carrasco, Quito",
        mapsUrl: "",
        embedSrc: "",
        phone: "593994900228",
      },
      {
        name: "Agencia El Pintado",
        address: "Av. Mariscal Sucre y Antonio Jaramillo (Sur de Quito)",
        mapsUrl: "",
        embedSrc: "",
        phone: "593998251265",
      },
      {
        name: "Agencia El Bosque",
        address: "Av. del Parque OE7-62 y, Quito 170144 (Norte de Quito)",
        mapsUrl: "https://maps.app.goo.gl/twRzjSQeTcGyiG4J8",
        embedSrc:
          "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3989.801927672479!2d-78.4963166!3d-0.1642427!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x91d59b3d9936cf09%3A0x7a51c68e9ed0777!2s%C3%81ngel%20de%20Oro!5e0!3m2!1sen!2sec!4v1767924121134!5m2!1sen!2sec",
        phone: "593988096159",
      },
    ],
  },
  {
    region: "Valle de los Chillos",
    agencies: [
      {
        name: "Agencia El Triángulo",
        address: "Av. Gral. Rumiñahui y Av. Ilálo, Valle de los Chillos",
        mapsUrl: "",
        embedSrc: "",
        phone: "593993895752",
      },
    ],
  },
  {
    region: "Sierra Norte",
    agencies: [
      {
        name: "Agencia Ibarra",
        address: "Simón Bolivar y Pedro Moncayo, Ibarra",
        mapsUrl: "",
        embedSrc: "",
        phone: "593983968022",
      },
      {
        name: "Agencia Otavalo",
        address: "Modesto Jaramillo y Juan Montalvo, Otavalo",
        mapsUrl: "",
        embedSrc: "",
        phone: "593997698637",
      },
    ],
  },
  {
    region: "Portoviejo",
    agencies: [
      {
        name: "Agencia Portoviejo",
        address: "Calle Quito y Ricaurte esquina, Portoviejo",
        mapsUrl: "",
        embedSrc: "",
        phone: "593987629557",
      },
    ],
  },
];

// Generadores de URL por defecto
const toMapsLink = (q) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
const toEmbedSrc = (q) =>
  `https://www.google.com/maps?q=${encodeURIComponent(q)}&output=embed`;

// WhatsApp helpers (tus números ya vienen como 593...)
const normalizePhoneDigits = (phone) => String(phone || "").replace(/\D/g, "");
const toWhatsAppLink = (phone, text = "") => {
  const digits = normalizePhoneDigits(phone);
  if (!digits) return "";
  const base = `https://wa.me/${digits}`;
  return text ? `${base}?text=${encodeURIComponent(text)}` : base;
};

export default function Location() {
  const [embedSrc, setEmbedSrc] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("");

  // Solo 1 acordeón abierto a la vez
  const firstRegion = useMemo(() => LOCATIONS[0]?.region || false, []);
  const [expandedRegion, setExpandedRegion] = useState(firstRegion);

  const handlePreview = (agency) => {
    const src = agency.embedSrc || toEmbedSrc(agency.address);
    setEmbedSrc(src);
    setSelectedLabel(`${agency.name} — ${agency.address}`);
  };

  // Controla el acordeón (y opcional: actualiza mapa al abrir región)
  const handleAccordionChange = (region, firstAgency) => (event, isExpanded) => {
    setExpandedRegion(isExpanded ? region : false);

    // ✅ OPCIONAL: al abrir una región, previsualiza la primera agencia
    if (isExpanded && firstAgency) {
      handlePreview(firstAgency);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3, border: "1px solid #eee" }}>
      <Typography sx={{ mb: 2 }}>
        Encuentra tu agencia más cercana. Abre el mapa o previsualiza aquí mismo.
      </Typography>

      <Grid container spacing={2} alignItems="flex-start">
        {/* Columna de listado */}
        <Grid item xs={12} md={6}>
          {LOCATIONS.map((group) => (
            <Accordion
              key={group.region}
              expanded={expandedRegion === group.region}
              onChange={handleAccordionChange(group.region, group.agencies?.[0])}
              disableGutters
              sx={{ border: "1px solid #eee", mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {group.region}
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                <List dense disablePadding>
                  {group.agencies.map((a) => {
                    const mapsHref = a.mapsUrl || toMapsLink(a.address);
                    const waHref = toWhatsAppLink(
                      a.phone,
                      `Hola 👋, quiero información de la ${a.name}.\nDirección: ${a.address}`
                    );

                    return (
                      <Box key={`${group.region}-${a.name}`}>
                        <ListItem alignItems="flex-start" sx={{ gap: 1 }}>
                          <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                            <PlaceIcon />
                          </ListItemIcon>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <ListItemText
                              primary={a.name}
                              secondary={a.address}
                              primaryTypographyProps={{ sx: { fontWeight: 700 } }}
                              secondaryTypographyProps={{
                                sx: { color: "text.secondary" },
                              }}
                            />

                            {/* Acciones debajo del texto (sin overlap) */}
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1}
                              sx={{
                                mt: 1,
                                justifyContent: { md: "flex-end" },
                                flexWrap: "wrap",
                              }}
                            >
                              <Button
                                size="small"
                                onClick={() => handlePreview(a)}
                                startIcon={<MapIcon />}
                                color="secondary"
                                variant="contained"
                              >
                                Previsualizar
                              </Button>

                              <Button
                                size="small"
                                variant="contained"
                                component="a"
                                href={mapsHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                startIcon={<NearMeIcon />}
                              >
                                Abrir en Maps
                              </Button>

                              <Button
                                size="small"
                                startIcon={<WhatsAppIcon />}
                                color="success"
                                variant="contained"
                                component="a"
                                href={waHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                disabled={!a.phone}
                              >
                                WhatsApp
                              </Button>
                            </Stack>
                          </Box>
                        </ListItem>

                        <Divider component="li" />
                      </Box>
                    );
                  })}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </Grid>

        {/* Columna de mapa (sticky en desktop) */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              position: { md: "sticky" },
              top: { md: 96 },
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid #ddd",
            }}
          >
            {embedSrc ? (
              <>
                <Box sx={{ p: 1, bgcolor: "#faf7ef" }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {selectedLabel}
                  </Typography>
                </Box>

                <Box sx={{ height: { xs: 260, sm: 320, md: 420, lg: 480 } }}>
                  <iframe
                    title="mapa-agencia"
                    src={embedSrc}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </Box>
              </>
            ) : (
              <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
                Selecciona una agencia y presiona <strong>Previsualizar</strong>{" "}
                para ver el mapa aquí.
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}
